import { capitalize, isNumber } from 'lodash';
import { z } from 'zod';

import { modelProperty, modelPropertyType } from '../api/models';

export const formatPropertyType = ({
  propertyType,
  propertySubType,
}: {
  propertyType?: string | null;
  propertySubType?: string | null;
}): string | null | undefined => {
  if (propertyType) {
    if (propertySubType) {
      return capitalize(
        [
          propertyType.trim(),
          propertySubType
            .trim()
            .replace(new RegExp(`^${propertyType.trim()}`, 'i'), '')
            .trim(),
        ]
          .map((segment) => segment.toLocaleLowerCase())
          .join(' ')
      ).replace(/(t\d+)/gi, (value) => value.toLocaleUpperCase());
    } else {
      return propertyType;
    }
  }

  return propertySubType;
};

export const getMainPropertyCategory = (
  property:
    | z.infer<typeof modelProperty.schema>['property']
    | z.infer<
        typeof modelPropertyType.schema
      >['propertyType']['properties']['edges'][number]['node']
): string | undefined => {
  const mainCategory = property.propertyTypes.edges.at(0);

  return mainCategory?.node.slug;
};

export const propertyIsRent = (
  property:
    | z.infer<typeof modelProperty.schema>['property']
    | z.infer<
        typeof modelPropertyType.schema
      >['propertyType']['properties']['edges'][number]['node']
) => getMainPropertyCategory(property) === 'location';

export const getMainPropertyPriceProp = (
  property:
    | z.infer<typeof modelProperty.schema>['property']
    | z.infer<
        typeof modelPropertyType.schema
      >['propertyType']['properties']['edges'][number]['node']
): 'priceSale' | 'priceRent' =>
  propertyIsRent(property) ? 'priceRent' : 'priceSale';

export const formatPrice = (value?: number | null) => {
  if (value) {
    const priceFormatter = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
    });

    return priceFormatter.format(value).replace(/\s/g, ' ');
  }

  return null;
};

export const getPropertyPrice = (
  property:
    | z.infer<typeof modelProperty.schema>['property']
    | z.infer<
        typeof modelPropertyType.schema
      >['propertyType']['properties']['edges'][number]['node']
): number | null | undefined => {
  return property.propertyMeta[getMainPropertyPriceProp(property)];
};

export const formatPropertyPrice = (
  property: z.infer<typeof modelProperty.schema>['property']
): string | null => {
  return formatPrice(getPropertyPrice(property));
};

export const sortPropertiesByPrice = (
  edges: z.infer<
    typeof modelPropertyType.schema
  >['propertyType']['properties']['edges'],

  order: 'ASC' | 'DESC' = 'ASC'
) =>
  edges &&
  [...edges].sort((a, b) => {
    const aPrice = getPropertyPrice(a.node);
    const bPrice = getPropertyPrice(b.node);

    if (!isNumber(aPrice)) {
      return 1;
    }

    if (!isNumber(bPrice)) {
      return -1;
    }

    if (order === 'ASC') {
      return aPrice - bPrice;
    }

    return bPrice - aPrice;
  });
